import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, CardText } from 'react-bootstrap';
import { useReadContract } from 'wagmi';
import communitiesContractABI from '../contracts/tgetherCommunities.json';
import communityConsensusContractABI from '../contracts/tgetherCommunityConsensus.json';
import communityRegistryABI from '../contracts/tgetherParameterGroupsRegistry.json'; // ABI for the Registry contract
import incentivesABI from '../contracts/tgetherIncentives.json'; // ABI for the Incentive contract
import VoteModal from './FormModals/VoteModal.tsx';
import { readContract } from '@wagmi/core';
import * as configs from '../config.tsx';

const communitiesContractAddress = process.env.REACT_APP_COMMUNITIES_CONTRACT_ADDRESS as `0x${string}`;
const communityConsensusContractAddress = process.env.REACT_APP_COMMUNITY_CONSENSUS_CONTRACT_ADDRESS as `0x${string}`;

interface ProposalInfo {
  proposer: string;
  communityName: string;
  timestamp: number;
  propType: number;
  lane: number;
  approveVotes: number;
  denyVotes: number;
  approveCreds: number;
  denyCreds: number;
  isActive: boolean;
  passed: boolean;
  voteClose?: number;
}

interface ProposalDetails {
  minCredsToProposeVote?: number;
  minCredsToVote?: number;
  maxCredsCountedForVote?: number;
  minProposalVotes?: number;
  memberAccessContract?: string;
  proposalTime?: number;
  proposalDelay?: number;
  isInviteOnly?: boolean;
  numReviewsForAcceptance?: number;
  credsNeededForReview?: number;
  percentAcceptsNeeded?: number;
  consensusTime?: number;
  upkeeped?: boolean;
  passed?: boolean;
}

function ProposalSearch({ value }: { value: string }) {
  const [propInfo, setPropInfo] = useState<ProposalInfo | null>(null);
  const [proposalDetails, setProposalDetails] = useState<ProposalDetails | null>(null);
  const [paramGroupDetails, setParamGroupDetails] = useState<any>(null);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Clear the state each time the `value` (proposal ID) changes
  useEffect(() => {
    setPropInfo(null);
    setProposalDetails(null);
    setParamGroupDetails(null);
    setLoading(true); // Start loading when new proposal ID is being loaded
  }, [value]);

  // Fetch the basic proposal information
  const { data: proposalInfo, isLoading: isPropInfoLoading } = useReadContract({
    address: communitiesContractAddress,
    abi: communitiesContractABI.abi,
    functionName: 'proposals',
    args: [value],
  });

  // Hook to fetch community proposal details
  const { data: communityProposalDetails } = useReadContract({
    address: communitiesContractAddress,
    abi: communitiesContractABI.abi,
    functionName: 'CommunityProposals',
    args: [value],
  });

  // Hook to fetch community consensus details
  const { data: customProposalDetails } = useReadContract({
    address: communityConsensusContractAddress,
    abi: communityConsensusContractABI.abi,
    functionName: 'CommunityConsensusProposals',
    args: [value],
  });

  const fetchGroupDetails = async (paramAddress: string) => {
    try {
      setLoading(true);
      let decodedABI: any[] = []; // Declare ABI variable
  
      // Check if the paramAddress matches the Incentives contract address
      if (paramAddress != process.env.REACT_APP_INCENTIVE_CONTRACT_ADDRESS) {
        // Use the local ABI for the incentives contract
        decodedABI = incentivesABI.abi; // Directly assign the ABI from the local file
      } else {
        // Fetch parameter group name by address from the registry
        const groupName = await readContract(configs.config, {
          address: process.env.REACT_APP_PARAMETER_GROUP_REGISTRY_ADDRESS as `0x${string}`,
          abi: communityRegistryABI.abi,
          functionName: 'parameterGroupNamesByAddress',
          args: [paramAddress],
        }) as string[];
  
        // Fetch the group details using the group name
        const groupDetails = await readContract(configs.config, {
          address: process.env.REACT_APP_PARAMETER_GROUP_REGISTRY_ADDRESS as `0x${string}`,
          abi: communityRegistryABI.abi,
          functionName: 'parameterGroupsByName',
          args: [groupName],
        }) as string[];
  
        // Decode the Base64 encoded ABI from the registry
        const encodedABI = groupDetails[2];
      
        decodedABI = JSON.parse(atob(encodedABI)); // Decode ABI and parse to JSON
      }
  
      // Now use the decoded ABI to fetch the proposal parameters
      const ProposalParams = await readContract(configs.config, {
        address: paramAddress as `0x${string}`,
        abi: decodedABI as any[], // Use the decoded or local ABI
        functionName: 'getProposal',
        args: [value], // Pass the proposal ID as the argument
      }) as any[];
  
      setParamGroupDetails(ProposalParams); // Set the proposal parameters
  
    } catch (err) {
      console.error('Error fetching parameter group details:', err);
    } finally {
      setLoading(false); // Ensure loading state is reset
    }
  };
  

  // Handle the dynamic rendering of the params
  const renderParams = (params: any) => {
    if (Array.isArray(params)) {
      return params.map((param, index) => (
        <CardText key={index}>
          <strong>Param {index}:</strong> {String(param)}
        </CardText>
      ));
    } else if (typeof params === 'object' && params !== null) {
      return Object.entries(params).map(([key, value], index) => (
        <CardText key={index}>
          <strong>{key}:</strong> {String(value)}
        </CardText>
      ));
    }
    return <CardText>No parameters available</CardText>;
  };

  // Effect to handle proposal info updates
  useEffect(() => {
    if (proposalInfo) {
      const parsedPropInfo: ProposalInfo = {
        proposer: (proposalInfo as any)[0],
        communityName: (proposalInfo as any)[1],
        timestamp: Number((proposalInfo as any)[2] ?? 0),  // Ensure conversion to 0 if undefined
        propType: Number((proposalInfo as any)[3] ?? 0),
        lane: Number((proposalInfo as any)[4] ?? 0),
        approveVotes: Number((proposalInfo as any)[5] ?? 0),
        denyVotes: Number((proposalInfo as any)[6] ?? 0),
        approveCreds: Number((proposalInfo as any)[7] ?? 0),
        denyCreds: Number((proposalInfo as any)[8] ?? 0),
        isActive: Boolean((proposalInfo as any)[9]),
        passed: Boolean((proposalInfo as any)[10]),
        voteClose: Number((proposalInfo as any)[2] ?? 0) + Number((proposalInfo as any).proposalTime ?? 0),
      };
      setPropInfo(parsedPropInfo);
      setLoading(false); // Stop loading after fetching proposal info
    }
  }, [proposalInfo]);

  const { data: ProposalContract } = useReadContract({
    address: communitiesContractAddress,
    abi: communitiesContractABI.abi,
    functionName: 'CustomProposals',
    args: [value],
  });

  useEffect(() => {
    if (propInfo?.propType === 1 && communityProposalDetails) {
      // Access the array elements using appropriate indices
      setProposalDetails({
        minCredsToProposeVote: Number((communityProposalDetails as any)[2] ?? 0),
        minCredsToVote: Number((communityProposalDetails as any)[3] ?? 0),
        maxCredsCountedForVote: Number((communityProposalDetails as any)[4] ?? 0),
        minProposalVotes: Number((communityProposalDetails as any)[5] ?? 0),
        memberAccessContract: (communityProposalDetails as any)[6] || '0x0000000000000000000000000000000000000000',
        proposalTime: Number((communityProposalDetails as any)[7] ?? 0),
        proposalDelay: Number((communityProposalDetails as any)[8] ?? 0),
        isInviteOnly: Boolean((communityProposalDetails as any)[9]),
      });
    } else if (propInfo?.propType === 2) {
      fetchGroupDetails(ProposalContract as string);

      if (ProposalContract === process.env.REACT_APP_COMMUNITY_CONSENSUS_CONTRACT_ADDRESS && customProposalDetails) {
        setProposalDetails({
          numReviewsForAcceptance: Number((customProposalDetails as any)[2] ?? 0),
          credsNeededForReview: Number((customProposalDetails as any)[3] ?? 0),
          percentAcceptsNeeded: Number((customProposalDetails as any)[4] ?? 0),
          consensusTime: Number((customProposalDetails as any)[5] ?? 0),
          upkeeped: Boolean((customProposalDetails as any)[6]),
          passed: Boolean((customProposalDetails as any)[7]),
        });
      }
    }
  }, [propInfo, communityProposalDetails, customProposalDetails]);

  const handleVoteClick = () => {
    setShowVoteModal(true);
  };

  const handleModalClose = () => {
    setShowVoteModal(false);
  };

  return (
    <Card>
      <Card.Header>
        <div className="community-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h2 className='text-info'> Prop ID: {value}</h2>
          {!loading && propInfo?.communityName && (
            <h5 className='text-secondary'> Community: {propInfo.communityName}</h5>
          )}
          <div style={{ marginTop: '10px' }}>
            <span style={{ color: propInfo?.isActive ? 'green' : 'red' }}>
              {propInfo?.isActive ? '✔️ Is Active' : '❌ No Longer Active'}
            </span>
            <br />
            <span style={{ color: propInfo?.passed ? 'green' : 'red' }}>
              {!propInfo?.isActive ? (propInfo?.passed ? '✔️ Passed' : '❌ Failed') : null}
            </span>
          </div>
        </div>
        <div  style={{ marginTop: '10px', alignItems:"center", display:"flex" }}>
          {propInfo?.isActive && (
              <Button variant="primary" onClick={handleVoteClick} style={{ margin:"auto" }}>
                Vote on this Proposal
              </Button>
            )}
        </div>
      </Card.Header>
      <Card.Body>
        {isPropInfoLoading && <div>Loading...</div>}
        {!isPropInfoLoading && propInfo?.proposer && (
          <>
            <Row>
              <Col style={{ padding: '30px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
                <h5 style={{
                  fontWeight: 'normal',
                  marginBottom: '15px',
                  backgroundColor: '#1a1d21',
                  color: '#ffffff',
                  textTransform: 'capitalize',
                  padding: '10px',
                  borderRadius: '4px'
                }}>
                  Proposal Information
                </h5>
                <Card.Text>Proposer: {propInfo.proposer}</Card.Text>
                <Card.Text>Community: {propInfo.communityName}</Card.Text>
                <Card.Text>Timestamp: {new Date(propInfo.timestamp * 1000).toLocaleString()}</Card.Text>
                <Card.Text>Approve Votes: {propInfo.approveVotes}</Card.Text>
                <Card.Text>Deny Votes: {propInfo.denyVotes}</Card.Text>
                <Card.Text>Approve Creds: {propInfo.approveCreds}</Card.Text>
                <Card.Text>Deny Creds: {propInfo.denyCreds}</Card.Text>
                <Card.Text> <strong>Vote Close: {new Date(propInfo.voteClose! * 1000).toLocaleString()} </strong></Card.Text>
              </Col>
              <Col style={{ padding: '30px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
                <h5 style={{
                  fontWeight: 'normal',
                  marginBottom: '15px',
                  backgroundColor: '#1a1d21',
                  color: '#ffffff',
                  textTransform: 'capitalize',
                  padding: '10px',
                  borderRadius: '4px'
                }}>
                  {propInfo.propType === 1 ? 'Community Proposal' : 'Custom Proposal'}
                </h5>
                {propInfo.propType === 1 && proposalDetails && (
                  <>
                    <Card.Text>Min Creds to Propose Vote: {proposalDetails.minCredsToProposeVote}</Card.Text>
                    <Card.Text>Min Creds to Vote: {proposalDetails.minCredsToVote}</Card.Text>
                    <Card.Text>Max Creds Counted for Vote: {proposalDetails.maxCredsCountedForVote}</Card.Text>
                    <Card.Text>Min Proposal Votes: {proposalDetails.minProposalVotes}</Card.Text>
                    <Card.Text>Member Access Contract: {proposalDetails.memberAccessContract}</Card.Text>
                    <Card.Text>Proposal Time: {proposalDetails.proposalTime}</Card.Text>
                    <Card.Text>Proposal Delay: {proposalDetails.proposalDelay}</Card.Text>
                    <Card.Text>Is Invite Only: {proposalDetails.isInviteOnly ? 'True' : 'False'}</Card.Text>
                  </>
                )}
                {propInfo.propType === 2 && proposalDetails && (
                  <>
                    <Card.Text>Number of Reviews for Acceptance: {proposalDetails.numReviewsForAcceptance}</Card.Text>
                    <Card.Text>Creds Needed for Review: {proposalDetails.credsNeededForReview}</Card.Text>
                    <Card.Text>Percent Accepts Needed: {proposalDetails.percentAcceptsNeeded}</Card.Text>
                    <Card.Text>Consensus Time: {proposalDetails.consensusTime}</Card.Text>
                    <Card.Text>Upkeeped: {proposalDetails.upkeeped ? 'Yes' : 'No'}</Card.Text>
                    <Card.Text>Passed: {proposalDetails.passed ? 'Yes' : 'No'}</Card.Text>
                  </>
                )}
                {propInfo.propType === 2 && paramGroupDetails && (
                  <>
                    {renderParams(paramGroupDetails)}
                  </>
                )}
              </Col>
            </Row>
          </>
        )}
      </Card.Body>

      {/* Vote Modal */}
      <VoteModal
        show={showVoteModal}
        onHide={handleModalClose}
        proposalId={value}
      />
    </Card>
  );
}

export default ProposalSearch;
